import BootstrapTable, { BootstrapTableProps } from 'react-bootstrap-table-next';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchedPodcasts, setCurrentPage, setTablePageSize } from '../../redux/actions';
import { ReactNode } from 'react';
import { fetchPodcasts, deletePodcast } from '../../utils/api';
import { Link } from 'react-router-dom';
import CustomSearch from '../ui/CustomSearch/CustomSearch';
import { PodcastType, UserInfoType } from '../../types/AppTypes';
import { RootState } from '../../redux/store';
import { useSelector } from 'react-redux';
import { formatOptions } from '../../utils/utils';

const { SearchBar } = Search;

type Props = {
  podcasts: PodcastType[];
};

const Table = ({ podcasts }: Props): JSX.Element => {

  const userInfo = useSelector((state: RootState) => state.userInfo.userInfo) as UserInfoType;

  const permissions = userInfo?.permissions;

  const columns = [
    {
      dataField: 'id',
      text: 'Podcast ID',
      headerAlign: 'center',
      align: 'center',
      sort: true,
    },
    {
      dataField: 'title',
      text: 'Title',
      headerAlign: 'center',
      align: 'center',
      sort: true,
    },
    {
      dataField: 'category',
      text: 'Category',
      headerAlign: 'center',
      align: 'center',
      sort: true,
    },
    {
      dataField: 'created',
      text: 'Created',
      formatter: (cell: string): string => {
        const utcDate = new Date(cell);

        const date = utcDate.toLocaleTimeString('en-US', formatOptions);

        return date;
      },
      headerAlign: 'center',
      align: 'center',
      sort: true,
    },
    {
      dataField: 'author',
      text: 'Author',
      headerAlign: 'center',
      align: 'center',
      sort: true,
    },
    {
      dataField: 'publisher',
      text: 'Publisher',
      headerAlign: 'center',
      align: 'center',
      sort: true,
    },
    {
      dataField: 'imageUrl',
      text: 'Image',
      headerAlign: 'center',
      align: 'center',
      formatter: (_cell: any, row: any) => (
        <div>
          <img src={_cell} width={60} height={60} alt={_cell?.substring(_cell?.lastIndexOf('/') + 1).split('.')[0]} />
        </div>
      ),
    },
    {
      dataField: 'numPublishedEpisode',
      text: 'Published Episodes',
      headerAlign: 'center',
      align: 'center',
      sort: true,
    },
    {
      dataField: 'numTotalEpisode',
      text: 'Total Episodes',
      headerAlign: 'center',
      align: 'center',
      sort: true,
    },
    {
      dataField: 'delete',
      hidden: permissions?.includes('podcasts.delete') || userInfo?.isAdmin? false : true,
      text: '',
      isDummyField: true,
      csvExport: false,
      formatter: (_cell: any, row: any) => (
        <div
          style={{ cursor: 'pointer', color: 'red' }}
          onClick={() => {
            if (window.confirm('Delete a podcast?')) {
              deletePodcast(row.id).then((res) => {
                dispatch(fetchedPodcasts(podcasts.filter((item) => item.id !== row.id)));
              });
            }
          }}
        >
          Delete
        </div>
      ),
      headerAlign: 'center',
      align: 'center',
      headerStyle: (colum, colIndex) => {
        return { width: '100px', textAlign: 'center' };
      },
    },
    {
      dataField: 'edit',
      hidden: permissions?.includes('podcasts.edit') || userInfo?.isAdmin ? false : true,
      text: '',
      isDummyField: true,
      csvExport: false,
      formatter: (_cell: any, row: any) => {
        return row.type === 'serial' ? (
          <>
            {' '}
            <Link to={`/podcast/${row.id}`}>Edit</Link> <br /> <Link to={`/episodes/${row.id}`}>Order</Link>{' '} 
          </>
        ) : (
          <>
            <Link to={`/podcast/${row.id}`}>Edit</Link>
          </>
        );
      },
      headerAlign: 'center',
      align: 'center',
      headerStyle: (colum, colIndex) => {
        return { width: '100px', textAlign: 'center' };
      },
    },
  ];
  const data = podcasts && podcasts.length ? podcasts : [];
  const dispatch = useAppDispatch();
  const tablePageSize = useAppSelector((state) => state.podcasts.tablePageSize);
  const currentPage = useAppSelector((state) => state.podcasts.currentPage);
  const searchQuery =
    localStorage.getItem('podcasts_search_input') !== null && localStorage.getItem('podcasts_search_input') !== ''
      ? localStorage.getItem('podcasts_search_input')
      : '';
  const pagination = paginationFactory({
    sizePerPage: parseInt(localStorage.getItem('podcasts-table-size') || '', 10) || tablePageSize,
    page: currentPage,
    onPageChange: (page) => {
      dispatch(setCurrentPage(page));
    },
    onSizePerPageChange: (sizePage) => {
      sizePerPageDocumentsChange(sizePage);
      dispatch(setTablePageSize(sizePage));
    },
  });

  const sizePerPageDocumentsChange = (sizePerPage: number) => {
    localStorage.setItem('podcasts-table-size', sizePerPage.toString());
  };

  return (
    <div style={{ marginBottom: '15px' }}>
      <ToolkitProvider
        keyField="id"
        data={data}
        columns={columns}
        striped
        hover
        condensed
        //sort={{ dataField: 'created', order: 'desc' }}
        search={{ defaultSearch: '' }}
      >
        {(props: {
          searchProps: {
            searchText: string | number;
            onSearch: (searchText: string | number) => void;
            onClear: () => void;
          };
          baseProps: JSX.IntrinsicAttributes &
            JSX.IntrinsicClassAttributes<BootstrapTable<any, number>> &
            Readonly<BootstrapTableProps<any, number>> &
            Readonly<{ children?: ReactNode }>;
        }) => (
          <div>
            <CustomSearch {...props.searchProps} defaultValue={searchQuery} localStorageItemName="podcasts_search_input" />
            <hr />
            <BootstrapTable {...props.baseProps} pagination={pagination} rowStyle={{ wordBreak: 'break-word' }} />
          </div>
        )}
      </ToolkitProvider>
    </div>
  );
};

export default Table;
